import React, { useState } from "react";
import { inject, observer } from "mobx-react";
import { QrCode } from "lucide-react";
import ConfirmModal from "../../Components/ConfirmModal";
import FeedQueueModal from "../../Components/FeedQueueModal";
import DjShareModal from "../../Components/DjShareModal";
import TrackAnalysisChip from "./TrackAnalysisChip";

// The dedicated DJ queue panel (right side in DJ mode). Shows the accumulated,
// explicitly-fed queue — independent from the normal playback playlist.
function DjQueuePanelInner({ DjStore }) {
  const queue = DjStore.queue;
  const [feedOpen, setFeedOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pendingLoad, setPendingLoad] = useState(null);

  const deckATrackId = DjStore.deckA?.track?.mediaId;
  const deckBTrackId = DjStore.deckB?.track?.mediaId;

  const requestLoadToDeck = (track, side) => {
    const deck = side === "A" ? DjStore.deckA : DjStore.deckB;
    if (deck.isPlaying) {
      setPendingLoad({ track, side });
      return;
    }
    DjStore.loadToDeck(track, side);
  };

  const confirmPendingLoad = () => {
    if (pendingLoad) DjStore.loadToDeck(pendingLoad.track, pendingLoad.side);
    setPendingLoad(null);
  };

  return (
    <div className="flex flex-col h-full bg-primary">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-800 bg-secondary flex-shrink-0">
        <span className="text-xs uppercase tracking-[0.18em] font-semibold text-text">
          DJ Queue
        </span>
        <span className="text-xs text-text-muted font-mono tabular-nums">
          {queue.length}
        </span>
        <div className="flex-1" />
        <button
          onClick={() => setShareOpen(true)}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded font-medium bg-neutral-700 text-text hover:bg-neutral-600 transition-colors"
          title="Crowd submit — share a QR link"
        >
          <QrCode size={14} /> Share
        </button>
        <button
          onClick={() => setFeedOpen(true)}
          className="px-2.5 py-1 text-xs rounded font-medium bg-accent text-white hover:opacity-90 transition-opacity"
        >
          + Feed
        </button>
        <button
          onClick={() => DjStore.clearQueue()}
          disabled={queue.length === 0}
          className="px-2.5 py-1 text-xs rounded font-medium text-text-muted hover:text-text hover:bg-neutral-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Clear
        </button>
      </div>

      {/* Queue rows */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden thin-scroll">
        {queue.length > 0 ? (
          <ul className="list-none m-0 p-0">
            {queue.map((track) => {
              const onDeckA = deckATrackId === track.mediaId;
              const onDeckB = deckBTrackId === track.mediaId;
              return (
                <li
                  key={track.mediaId}
                  className={`
                    flex border-b border-neutral-900 transition-colors group hover:bg-neutral-900
                    ${onDeckA ? "border-l-2 border-l-blue-500" : ""}
                    ${onDeckB ? "border-l-2 border-l-pink-500" : ""}
                  `}
                >
                  {/* Album art */}
                  <div
                    className="w-14 h-14 md:w-16 md:h-16 bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: `url(${track.albumArtUrl})` }}
                  />

                  {/* Track details */}
                  <div className="flex-1 flex flex-col justify-center min-w-0 px-2 md:px-3">
                    <p className="m-0 text-text text-sm md:text-base truncate overflow-hidden">
                      {track.title}
                    </p>
                    <p className="m-0 text-text-muted text-xs md:text-sm truncate">
                      {track.artist}
                    </p>
                  </div>

                  {/* Analysis + deck load + remove */}
                  <div className="flex items-center gap-2 pr-2 md:pr-3 flex-shrink-0">
                    <TrackAnalysisChip features={DjStore.featureCache?.[track.mediaId]} />
                    <div className="flex gap-1">
                      <button
                        onClick={() => requestLoadToDeck(track, "A")}
                        className={`px-2 py-1 text-xs rounded font-medium ${onDeckA ? "bg-blue-600 text-white" : "bg-neutral-700 hover:bg-blue-500 hover:text-white"}`}
                        title="Load to Deck A"
                      >
                        A
                      </button>
                      <button
                        onClick={() => requestLoadToDeck(track, "B")}
                        className={`px-2 py-1 text-xs rounded font-medium ${onDeckB ? "bg-pink-600 text-white" : "bg-neutral-700 hover:bg-pink-500 hover:text-white"}`}
                        title="Load to Deck B"
                      >
                        B
                      </button>
                    </div>
                    <button
                      onClick={() => DjStore.removeFromQueue(track.mediaId)}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-text-muted hover:text-text hover:bg-neutral-800 transition-colors text-lg leading-none"
                      title="Remove from queue"
                      aria-label="Remove from queue"
                    >
                      ×
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center h-full pt-20 px-4 text-center">
            <p className="text-gray-400 text-2xl md:text-3xl mb-2">Queue empty</p>
            <p className="text-gray-400 text-sm md:text-base">
              Feed a playlist or a song to get started.
            </p>
            <button
              onClick={() => setFeedOpen(true)}
              className="mt-4 px-4 py-2 text-sm rounded font-medium bg-accent text-white hover:opacity-90 transition-opacity"
            >
              + Feed
            </button>
          </div>
        )}
      </div>

      <FeedQueueModal isOpen={feedOpen} onClose={() => setFeedOpen(false)} />
      <DjShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)} />

      <ConfirmModal
        isOpen={!!pendingLoad}
        onClose={() => setPendingLoad(null)}
        onConfirm={confirmPendingLoad}
        title={`Replace Deck ${pendingLoad?.side ?? ""}?`}
        message={pendingLoad ? `"${pendingLoad.track.title}" will replace the currently playing track on Deck ${pendingLoad.side}.` : ""}
        confirmLabel="Replace"
      />
    </div>
  );
}

export default inject("DjStore")(observer(DjQueuePanelInner));
